import React from "react";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";
import { Line } from "react-chartjs-2";
import Icon from "@mui/material/Icon";
import { useMemo } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import configs from "examples/Charts/LineCharts/ReportsLineChart/configs";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartDataLabels // register plugin
);

const splitTimestampParts = (value = "") => {
  if (!value) return [null, null];
  const normalized = String(value).trim();
  if (!normalized) return [null, null];

  if (normalized.includes("\n")) {
    const [first, ...rest] = normalized
      .split("\n")
      .map((part) => part.trim())
      .filter(Boolean);
    return [first || null, rest.join(" ") || null];
  }

  const isoMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s](.*))?$/);
  if (isoMatch) {
    return [isoMatch[1], isoMatch[2] ? isoMatch[2].replace(/Z$/, "").trim() || null : null];
  }

  const separators = [" | ", " - ", " – "];
  for (const separator of separators) {
    const index = normalized.indexOf(separator);
    if (index !== -1) {
      const first = normalized.slice(0, index).trim();
      const second = normalized.slice(index + separator.length).trim();
      return [first || normalized, second || null];
    }
  }

  const parts = normalized.split(/\s+/);
  if (parts.length > 1) {
    return [parts[0], parts.slice(1).join(" ") || null];
  }

  return [normalized, null];
};

function EnvChart({
  color,
  title,
  chart,
  chartHeight,
  timestamp,
  env_ftemp_sensor,
  env_ftemp_reading,
  env_ftemp_status,
  env_ftemp_target = [],
}) {
  const datasetsWithFallback = useMemo(() => {
    const datasetsInput = chart?.datasets ?? {};
    const datasetArray = Array.isArray(datasetsInput) ? datasetsInput : [datasetsInput];

    const enhanced = datasetArray.map((dataset) => {
      if (!dataset) return dataset;
      const labelKey = typeof dataset.label === "string" ? dataset.label.toLowerCase() : "";
      if (
        labelKey !== "floor temp" ||
        !Array.isArray(dataset.metadata) ||
        !Array.isArray(env_ftemp_target) ||
        env_ftemp_target.length === 0
      ) {
        return dataset;
      }

      let modified = false;
      const metadata = dataset.metadata.map((entry = {}) => {
        if (entry && entry.target !== undefined && entry.target !== null) {
          return entry;
        }

        modified = true;
        return { ...entry, target: env_ftemp_target };
      });

      return modified ? { ...dataset, metadata } : dataset;
    });

    return Array.isArray(datasetsInput) ? enhanced : enhanced[0] || {};
  }, [chart, env_ftemp_target]);

  const { data, options } = configs((chart && chart.labels) || [], datasetsWithFallback);
  const [timestampDate, timestampTime] = useMemo(() => splitTimestampParts(timestamp), [timestamp]);

  return (
    <Card sx={{ height: "100%", pb: { xs: "1cm", sm: 0 } }}>
      <MDBox padding="1rem">
        {useMemo(
          () => (
            <MDBox
              variant="gradient"
              bgColor={color}
              borderRadius="lg"
              coloredShadow={color}
              py={2}
              pr={0.5}
              mt={-5}
              height={chartHeight}
            >
              <Line data={data} options={options} />
            </MDBox>
          ),
          [chart, color]
        )}
        <MDBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: 3,
            pb: { xs: 4, sm: 1 },
            px: 1,
          }}
        >
          <MDBox
            sx={{
              display: "flex",
              flexDirection: { xs: "row", sm: "column" },
              alignItems: { xs: "flex-start", sm: "flex-start" },
              justifyContent: { xs: "space-between", sm: "flex-start" },
              gap: { xs: 1, sm: 0.75 },
              width: "100%",
            }}
          >
            <MDBox
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: { xs: 0.5, sm: 0.75 },
              }}
            >
              <MDTypography variant="h6" textTransform="capitalize">
                {title}
              </MDTypography>
              <MDTypography variant="h6" textTransform="capitalize">
                Device: {env_ftemp_sensor}
              </MDTypography>
            </MDBox>
            {timestamp ? (
              <>
                <MDBox
                  sx={{
                    display: { xs: "flex", sm: "none" },
                    flexDirection: "column",
                    alignItems: "flex-end",
                    fontSize: "0.95rem",
                    color: "text.secondary",
                    textAlign: "right",
                    ml: "auto",
                  }}
                >
                  <MDTypography variant="button" color="text">
                    {timestampDate || timestamp}
                  </MDTypography>
                  {timestampTime ? (
                    <MDTypography variant="button" color="text" fontWeight="light" sx={{ mt: 0.1 }}>
                      {timestampTime}
                    </MDTypography>
                  ) : null}
                </MDBox>
                <MDBox
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                    gap: 0.75,
                    fontSize: "0.95rem",
                    color: "text.secondary",
                    mt: "0.5cm",
                  }}
                >
                  <Icon sx={{ fontSize: "1.2rem" }}>schedule</Icon>
                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="light"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {timestamp}
                  </MDTypography>
                </MDBox>
              </>
            ) : null}
          </MDBox>

          <MDBox
            sx={{
              position: { xs: "static", sm: "absolute" },
              bottom: { sm: 90 },
              right: { sm: 60 },
              mt: { xs: 2, sm: 0 },
              display: "flex",
              flexDirection: { xs: "row", sm: "row" },
              alignItems: { xs: "center", sm: "center" },
              gap: { xs: 1.5, sm: 0.75 },
              width: { xs: "100%", sm: "auto" },
              overflowX: { xs: "auto", sm: "visible" },
              pr: { xs: 1, sm: 0 },
              whiteSpace: { xs: "nowrap", sm: "normal" },
              transform: { xs: "translateY(-0.5cm)", sm: "none" },
            }}
          >
            <MDTypography sx={{ fontSize: "1rem", minWidth: "55px" }} color="textSecondary">
              Temp:
            </MDTypography>
            <MDBox
              component="span"
              sx={{
                px: 1.2,
                py: 0.2,
                borderRadius: "4px",
                backgroundColor: (theme) => {
                  switch ((env_ftemp_status || "").toLowerCase()) {
                    case "green":
                      return theme.palette.success.main;
                    case "amber":
                      return theme.palette.warning.main;
                    case "red":
                      return theme.palette.error.main;
                    default:
                      return theme.palette.grey[400];
                  }
                },
                color: (theme) => theme.palette.common.white,
                fontWeight: "bold",
                fontSize: "1rem",
              }}
            >
              {env_ftemp_reading !== undefined ? `${env_ftemp_reading}\u00B0C` : "--"}
            </MDBox>
            {Array.isArray(env_ftemp_target) && env_ftemp_target.length === 2 && (
              <MDTypography sx={{ fontSize: "1rem" }} color="textSecondary">
                ({env_ftemp_target[0]}-{env_ftemp_target[1]})
              </MDTypography>
            )}
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of ReportsLineChart
EnvChart.defaultProps = {
  color: "info",
  description: "",
  canvasHeight: "12.5rem",
};

// Typechecking props for the ReportsLineChart
EnvChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
  chartHeight: PropTypes.string,
  timestamp: PropTypes.string,
  env_ftemp_sensor: PropTypes.string,
  env_ftemp_reading: PropTypes.number,
  env_ftemp_status: PropTypes.string,
  env_ftemp_target: PropTypes.arrayOf(PropTypes.number),
};

export default EnvChart;
